import * as React from "react";
import { connect } from "react-redux";

import { Grid, Table, Loader, Icon, Modal } from "semantic-ui-react";

import { isRequestActive } from "redux-request-loading";

import { Input } from "@neworbit/simpleui-input";

import { CASE_STATUSES, PRODUCT_TYPES } from "@teamprevent/common/model";
import { getCaseInformation } from "@teamprevent/common/ohCase/model";
import { isSystemAdminUser } from "@teamprevent/common/auth/roles";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { useCustomMedicalTypes } from "../../../customMedicalTypes/hooks/useCustomMedicalTypes";

import { ohCasesSelector } from "../selectors";
import { PartialOhCaseListModel } from "../model";
import { ApplicationState } from "../../../applicationState";
import { routeIdSelector as employeeRecordIdSelector } from "../../employeeRecord/selectors";
import { LOAD_OH_CASES } from "../actiontypes";
import { CurrentUser } from "../../../auth";

import { loadOhCases } from "../actions";

import { CreateCaseButton } from "./CreateCaseButton";
import { MatchEmployeeModal } from "./MatchEmployeeModal";

import { Link } from "redux-little-router";

interface AllProps {
    ohCases: PartialOhCaseListModel[];
    path: string;
    loading: boolean;
    currentUser: CurrentUser;
}

const caseTypesOptions = PRODUCT_TYPES.map(type => ({
    key: type.value,
    value: type.value,
    text: type.displayName
}));

const caseStatusesOptions = CASE_STATUSES.map(status => ({
    key: status.value,
    value: status.value,
    text: status.displayName
}));

export const AllUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const dispatch = useDispatch();
    const customerId = useSelector((state: ApplicationState) => state.router.params.customerId);
    const employeeRecordId = useSelector((state: ApplicationState) => state.router.params.employeeRecordId);
    const [caseTypes, setCaseTypes] = React.useState<string[]>([]);
    const [caseStatuses, setCaseStatuses] = React.useState<string[]>([]);
    const [filteredOhCases, setFilteredOhCases] = React.useState<PartialOhCaseListModel[]>(props.ohCases);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [ohCaseForMatching, setOhCaseForMatching] = React.useState<PartialOhCaseListModel | null>();
    const { customMedicalTypes, isLoaded: areCustomMedicalTypesLoaded } = useCustomMedicalTypes();

    React.useEffect(() => {
        dispatch(loadOhCases({ customerId, employeeRecordId }));
    }, [customerId, dispatch, employeeRecordId]);

    React.useEffect(() => {
        if (!props.loading) {
            setFilteredOhCases(props.ohCases);
        }
    }, [props]);

    React.useEffect(() => {
        if (caseTypes.length > 0 && caseStatuses.length > 0) {
            const filtered = props.ohCases.filter(c => caseTypes.includes(c.caseType) && caseStatuses.includes(c.caseStatus));
            setFilteredOhCases(filtered);
        } else if (caseTypes.length > 0 && caseStatuses.length === 0) {
            const filtered = props.ohCases.filter(c => caseTypes.includes(c.caseType));
            setFilteredOhCases(filtered);
        } else if (caseTypes.length === 0 && caseStatuses.length > 0) {
            const filtered = props.ohCases.filter(c => caseStatuses.includes(c.caseStatus));
            setFilteredOhCases(filtered);
        } else {
            setFilteredOhCases(props.ohCases);
        }
    }, [caseTypes, caseStatuses, props.ohCases]);

    const openMatchingModal = (ohCase: PartialOhCaseListModel) => {
        setOhCaseForMatching(ohCase);
        setIsOpen(true);
    };

    const closeMatchingModal = React.useCallback(() => {
        setOhCaseForMatching(null);
        setIsOpen(false);
    }, []);

    if (areCustomMedicalTypesLoaded === false)
    {
        return <Loader active />;
    }

    return (
        <>
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column width={12}>
                        <h1>Cases</h1>
                    </Grid.Column>
                    <Grid.Column width={4} textAlign="right">
                        <CreateCaseButton />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell width={1}>Case ID</Table.HeaderCell>
                                    <Table.HeaderCell width={3}>Case Type</Table.HeaderCell>
                                    <Table.HeaderCell>Created</Table.HeaderCell>
                                    <Table.HeaderCell>Last Updated</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Case Status</Table.HeaderCell>
                                    <Table.HeaderCell width={4}>Case Information</Table.HeaderCell>
                                    {isSystemAdminUser(props.currentUser) && <Table.HeaderCell width={1}></Table.HeaderCell>}
                                </Table.Row>
                                <Table.Row>
                                    <Table.HeaderCell />
                                    <Table.HeaderCell>
                                        <Input.DropdownMulti
                                            multiple
                                            value={caseTypes}
                                            onChange={setCaseTypes}
                                            options={caseTypesOptions}
                                            search
                                            placeholder="Search..."
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                    <Table.HeaderCell />
                                    <Table.HeaderCell>
                                        <Input.DropdownMulti
                                            multiple
                                            value={caseStatuses}
                                            onChange={setCaseStatuses}
                                            options={caseStatusesOptions}
                                            search
                                            placeholder="Search..."
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                    {isSystemAdminUser(props.currentUser) && <Table.HeaderCell />}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {filteredOhCases?.map(ohCase => (
                                    <Table.Row key={ohCase.id}>
                                        <Table.Cell selectable>
                                            <Link href={`${props.path}/${ohCase.id}`}>
                                                {ohCase.caseId}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {ohCase.caseType}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {ohCase.created && ohCase.created.format("L")}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {ohCase.lastUpdated && ohCase.lastUpdated.format("L")}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {ohCase.caseStatus}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="ellipsis-line-clamp">
                                                <Link href={`${props.path}/${ohCase.id}`}>
                                                    {getCaseInformation(ohCase, customMedicalTypes)}
                                                </Link>
                                            </div>
                                        </Table.Cell>
                                        {isSystemAdminUser(props.currentUser) &&
                                            <Table.Cell>
                                                <div>
                                                    <Icon
                                                        color="blue"
                                                        name="minus square outline"
                                                        onClick={() => openMatchingModal(ohCase)}
                                                        size="big"
                                                        link={true}
                                                        disabled={!ohCase.matchedReferralId} />
                                                </div>
                                            </Table.Cell>
                                        }
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Grid.Column>
                </Grid.Row>
                {!props.ohCases?.length && props.loading && <Grid.Row><Loader active /></Grid.Row>}
            </Grid>
            <Modal
                open={isOpen}
            >
                <MatchEmployeeModal ohCase={ohCaseForMatching} onClose={closeMatchingModal} />
            </Modal>
        </>
    );
};

export function mapStateToProps(state: ApplicationState): AllProps {
    return {
        path: state.router.pathname!,
        loading: isRequestActive(state, LOAD_OH_CASES),
        ohCases: ohCasesSelector(state).filter(c => c.employeeRecordId === employeeRecordIdSelector(state)),
        currentUser: state.currentUser
    };
}

export const All = connect(mapStateToProps)(AllUnconnected);
